import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

// INTERFACES

// SERVICES
import { environment } from '@app/env-service';

import {
  RfqLot,
  RfqLotLabel,
  RfqLotLabelItems,
  RfqStockLogInterface,
} from '../store/rfq-lots.model';

@Injectable({
  providedIn: 'root',
})
export class RfqLotService {
  private readonly _rfqLots = new BehaviorSubject<RfqLot[]>(<RfqLot[]>[]);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _printLabelBlob = new BehaviorSubject<any>(<any>[]);
  private readonly _rfqLotStockLogs = new BehaviorSubject<RfqStockLogInterface[]>(
    <RfqStockLogInterface[]>[],
  );

  private rfqLots :any[] = [];

  loading$ = this._loading.asObservable();
  rfqLots$ = this._rfqLots.asObservable();
  printLabelBlob$ = this._printLabelBlob.asObservable();
  rfqLotStockLogs$ = this._rfqLotStockLogs.asObservable();

  constructor(private http :HttpClient) {}

  getByRfqIds(params :any) :Observable<any> {
    this._loading.next(true);

    return this.http
      .get(`${environment.url}/rfq-lots/rfq-ids`, { params: params })
      .pipe(
        tap((resp :any) => {
          this._rfqLots.next(resp.data);
          this._loading.next(false);
        }),
      );
  }

  create(rfqLot :any) :Observable<any> {
    return this.http.post(`${environment.url}/rfq-lots`, rfqLot).pipe(
      map((response :any) => {
        this._rfqLots.next([...this.rfqLots, response]);

        return response;
      }),
      catchError((response :any) => {
        return throwError(response.error);
      }),
    );
  }

  exportLabel(payload :RfqLotLabelItems) :Observable<any> {
    this._loading.next(true);

    return this.http
      .post(`${environment.url}/rfq-lots/print-label`, payload, {
        responseType: 'blob',
      })
      .pipe(
        map((response :Blob) => {
          this._loading.next(false);
          this._printLabelBlob.next(response);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }

  downloadFile(blob :Blob, fileName :string) :void {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 7000);
  }

  getByLotNumber(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${environment.url}/rfq-lots/lot-number`, { params })
      .subscribe(
        (response :any) => {
          this._loading.next(false);
          this._rfqLots.next(response.data);
        },
        (response :any) => {
          this._loading.next(false);
          throwError(response.error);
        },
      );
  }

  getTransactionsByLotNumber(params ?:{
    [param :string] :string | string[];
  }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${environment.url}/rfq-lots/lot-tracking`, { params })
      .subscribe(
        (response :any) => {
          this._loading.next(false);
          this._rfqLotStockLogs.next(response ?? []);
        },
        (response :any) => {
          this._loading.next(false);
          throwError(response);
        },
      );
  }

  bulkCreate(payload :any) :Observable<any> {
    return this.http.post(`${environment.url}/rfq-lots/bulk-create`, payload).pipe(
      map((lots :any) => {
        const rfqLots = this._rfqLots.value;
        const newRfqLots = lots.filter(
          (newLot) => !rfqLots.some((lot) => lot.id === newLot.id),
        );
        const updatedRfqLots = lots.concat(newRfqLots);
        this._rfqLots.next([...rfqLots, ...updatedRfqLots]);

        return lots;
      }),
      catchError((response :any) => {
        return throwError(response.error);
      }),
    );
  }
}
